import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { confirmDialog } from "../../../components/ConfirmDialog/confirmDialog";
import { addIdentificationType, getAllWorkerIdentificationTypes, updateWorkerIdentificationType, deleteWorkerIdentification } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faX } from "@fortawesome/free-solid-svg-icons";

const IdentificationType = () => {
    const { state } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [identificationType, setIdentificationType] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentItem, setCurrentItem] = useState<any>(null);
    const [formData, setFormData] = useState({
        name: "",
        code: "",
        description: "",
        isActive: false,
        isRequired: false,
        maxLength: 0,
        validationRegex: ""
    });

    const handleAddClick = () => {
        setModalMode('add');
        setFormData({
            name: "",
            code: "",
            description: "",
            isActive: false,
            isRequired: false,
            maxLength: 0,
            validationRegex: ""
        });
        setCurrentItem(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentItem(params.row);
        setFormData({
            name: params.row.name || "",
            code: params.row.code || "",
            description: params.row.description || "",
            isActive: params.row.isActive || false,
            isRequired: params.row.isRequired || false,
            maxLength: params.row.maxLength || 0,
            validationRegex: params.row.validationRegex || ""
        });
        setIsModalOpen(true);
    };

    const handleDialogClose = () => {
        setIsModalOpen(false);
        setFormData({
            name: "",
            code: "",
            description: "",
            isActive: false,
            isRequired: false,
            maxLength: 0,
            validationRegex: ""
        });
        setCurrentItem(null);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Name cannot be empty');
            return;
        }
        if (!formData.code.trim()) {
            toast.error('Code cannot be empty');
            return;
        }

        if (modalMode === 'add') {
            const response = await addIdentificationType(state.user?.token, formData);
            if (response) {
                toast.success('Identification Type added successfully');
                getAllIdentificationTypes();
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentItem) {
                const response = await updateWorkerIdentificationType(state.user?.token, {
                    id: currentItem.id,
                    ...formData
                });
                if (response) {
                    toast.success('Identification Type updated successfully');
                    getAllIdentificationTypes();
                    handleDialogClose();
                } else {
                    toast.error('Failed to update identification type');
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        try {
            const response: any = await deleteWorkerIdentification(state.user?.token, id);
            if (response?.forbidden) {
                toast.warn('You don\'t have permission to delete this identification type');
                return;
            }
            if (response) {
                toast.success('Identification Type deleted successfully');
                getAllIdentificationTypes();
            }
        } catch (error) {
            console.error("Failed to delete identification type:", error);
            toast.error('Failed to delete identification type');
        }
    };

    const getAllIdentificationTypes = async () => {
        try {
            setLoading(true);
            const response = await getAllWorkerIdentificationTypes(state.user?.token);
            if (response && response.data) {
                setIdentificationType(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch identification types:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllIdentificationTypes();
    }, []);

    const identificationTypeColumns: any = [
        {
            field: "name",
            headerName: "Name",
            flex: 1
        },
        {
            field: "code",
            headerName: "Code",
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },
        {
            field: "description",
            headerName: "Description",
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },
        {
            field: "maxLength",
            headerName: "Max Length",
            flex: 0.5,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: "isActive",
            headerName: "Is Active",
            flex: 0.5,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <div>
                    {params.row.isActive ? (
                        <FontAwesomeIcon icon={faCheck} size={"lg"} color={"green"} />
                    ) : (
                        <FontAwesomeIcon icon={faX} size={"lg"} color={"red"} />
                    )}
                </div>
            ),
        },
        {
            field: "isRequired",
            headerName: "Is Required",
            flex: 0.5,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <div>
                    {params.row.isRequired ? (
                        <FontAwesomeIcon icon={faCheck} size={"lg"} color={"green"} />
                    ) : (
                        <FontAwesomeIcon icon={faX} size={"lg"} color={"red"} />
                    )}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.8,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params)}
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        style={{ boxShadow: "none" }}
                        onClick={() =>
                            confirmDialog({
                                title: `Are you sure you want to delete ${params.row.name}?`,
                                onOk: () => handleDelete(params.row.id),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            })
                        }
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Box
            sx={{
                p: 1,
                height: '100%',
                minHeight: '100vh'
            }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1400px',
                    margin: '0 auto'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mb: 3
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        style={{ boxShadow: 'none' }}
                    >
                        Add Identification Type
                    </Button>
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        height: '600px',
                        margin: "0 auto",
                        "@media (max-width: 800px)": {
                            overflowX: "auto",
                            margin: 1,
                            minWidth: "95%",
                        },
                    }}
                >
                    <DataGrid
                        rows={identificationType}
                        columns={identificationTypeColumns}
                        disableRowSelectionOnClick
                        loading={loading}
                        sx={{
                            "@media (max-width: 700px)": {
                                minWidth: "100%",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                color: "#333",
                                fontWeight: "bold",
                                fontSize: 15,
                                backgroundColor: '#f0f0f0',
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f1f1f1",
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Dialog
                open={isModalOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add Identification Type' : 'Edit Identification Type'}
                </DialogTitle>
                <DialogContent>
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr" }} gap={2} mt={1}>
                        <TextField
                            label="Name"
                            fullWidth
                            size="small"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                        <TextField
                            label="Code"
                            fullWidth
                            size="small"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            required
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            }
                            label="Is Active"
                            sx={{ ml: 1 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isRequired}
                                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                                />
                            }
                            label="Is Required"
                            sx={{ ml: 1 }}
                        />
                        <TextField
                            label="Max Length"
                            type="number"
                            fullWidth
                            size="small"
                            value={formData.maxLength}
                            onChange={(e) => setFormData({ ...formData, maxLength: parseInt(e.target.value) || 0 })}
                        />
                        <TextField
                            label="Validation Regex"
                            fullWidth
                            size="small"
                            value={formData.validationRegex}
                            onChange={(e) => setFormData({ ...formData, validationRegex: e.target.value })}
                        />
                        <TextField
                            label="Description"
                            multiline
                            size="small"
                            rows={4}
                            fullWidth
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{ boxShadow: 'none' }}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IdentificationType;
